<script setup lang="ts">
import { EPointerType } from '@/enums/pointer.enum';
import { ref, watch } from 'vue';
import RatingStar from './RatingStar.vue';

const emit = defineEmits<{
  'update:clicked': [rating: number];
  'update:hovered': [star: number | null];
}>();

const props = defineProps<{
  rating?: number;
}>();

const hoveredStar = ref<number | null>(null);
const selectedStar = ref<number | null>(null);

watch(
  () => props.rating,
  (val) => {
    selectedStar.value = val ?? null;
    hoveredStar.value = val ?? null;
  },
  { immediate: true },
);

const isStarActive = (starIndex: number): boolean => {
  if (hoveredStar.value !== null) {
    return starIndex <= hoveredStar.value;
  }
  return selectedStar.value !== null && starIndex <= selectedStar.value;
};

const isStarDimmed = (starIndex: number): boolean => {
  if (hoveredStar.value === null || selectedStar.value === null) return false;
  if (hoveredStar.value >= selectedStar.value) return false;
  return starIndex > hoveredStar.value && starIndex <= selectedStar.value;
};

const handleStarHover = (starIndex: number): void => {
  hoveredStar.value = starIndex;
  emit('update:hovered', starIndex);
};

const handlePointerEnter = (e: PointerEvent, starIndex: number): void => {
  if (e.pointerType === EPointerType.MOUSE) handleStarHover(starIndex);
};

const handleStarLeave = (): void => {
  hoveredStar.value = null;
  emit('update:hovered', null);
};

const handleStarClick = (starIndex: number): void => {
  if (selectedStar.value === starIndex) {
    selectedStar.value = null;
    emit('update:clicked', 0);
  } else {
    selectedStar.value = starIndex;
    emit('update:clicked', starIndex);
  }
};
</script>

<template>
  <div
    class="flex items-center justify-center border border-quaternary px-lg py-md rounded-group text-tertiary gap-sm"
    @pointerleave="handleStarLeave"
  >
    <RatingStar
      v-for="index in 5"
      :key="index"
      :is-active="isStarActive(index)"
      :is-dimmed="isStarDimmed(index)"
      class="w-[48px] h-[48px] cursor-pointer transition-transform hover:scale-110"
      @pointerenter="(e: PointerEvent) => handlePointerEnter(e, index)"
      @pointerdown.prevent="handleStarClick(index)"
    />
  </div>
</template>
